import { DisbursementOptions } from 'config/const';
import { ICurrentEmployee } from 'store/types';
import { DisbursementMethod, EmployeeRole, EmployeeStatus, EmployeeType, HoldbackType, IEmployee, IEmployeeSite, IEmployerSite, IHoldbackFixed, IHoldbackPercent, ISite, ISiteRolePolicy, SiteRole } from 'types';
import { sortAlphaNumerically } from './arrays';

/**
 * Checks if a site uses holdbacks based on the provided holdback settings.
 *
 * This function determines if a site has an active holdback by inspecting the provided
 * holdback settings. A holdback is considered active if:
 * - The `holdbackSettings` is defined.
 * - If the holdback type is `FIXED`, the holdback amount must be greater than zero.
 * - For other types, the holdback is considered active as long as `holdbackSettings` is present.
 *
 * @param {IHoldbackFixed | IHoldbackPercent} [holdbackSettings] - The settings defining the holdback rules for the site.
 * It could either be a fixed or percentage-based holdback configuration.
 * @returns {boolean} `true` if the site has an active holdback, otherwise `false`.
 */
export const siteUsesHoldbacks = (holdbackSettings?: IHoldbackFixed | IHoldbackPercent): boolean => {
  if (!holdbackSettings) return false;

  return holdbackSettings.type !== HoldbackType.FIXED || holdbackSettings.amount !== 0;
};

/**
 * Checks if a site is configured to use withholdings.
 *
 * @param {ISite} site - The site object to check.
 * @returns {boolean} `true` if the site has withholdings allowed and applied, otherwise `false`.
 */
export const siteUsesWithholdings = (site: ISite): boolean => !!(site?.settings.withholdings?.allowed && site.settings.withholdings.apply);

interface ISiteSwitcherInfo {
  options: IEmployerSite[];
  currentSite?: IEmployerSite;
}

/**
 * Takes an array of employer sites and returns an array of selectable options sorted by site name and the currently selected site
 *
 * Today the only caller is `ConnectedSiteSelector`, which passes the output of
 * `selectCurrentEmployeeManagedSites` — already sorted by the same comparator, so this sort is a
 * no-op on that path. It is kept deliberately: this function promises sorted options to whoever
 * calls it, and `SiteSelector` renders whatever order it receives. Dropping the sort here would
 * make the ordering depend on where the caller happened to source its sites.
 *
 * @param currentEmployeeSites - IEmployerSite array
 * @param currentSiteId - siteId string
 * @returns SiteSwitcherInfo
 */
export const getSiteSwitcherInfo = (currentEmployeeSites: IEmployerSite[], currentSiteId?: string): ISiteSwitcherInfo => {
  const sortedSiteOptions = [...currentEmployeeSites].sort(sortAlphaNumerically('name'));
  return {
    options: sortedSiteOptions,
    currentSite: sortedSiteOptions.find((site) => site.id === currentSiteId),
  };
};

/**
 * Get list of sites the user was never active on or is disabled at.
 *
 * @param sites - IEmployerSite array
 * @param user - IEmployee object
 * @returns Filtered list of employer sites
 */
export const getAvailableEmployerSites = (sites: IEmployerSite[], user?: IEmployee | null): IEmployerSite[] => {
  if (!user?.sites) return sites;

  const filteredSites = sites.filter((site) => {
    const userSite = user.sites.find((s) => s.id === site.id);
    return !userSite || userSite.status === EmployeeStatus.DISMISSED;
  });
  return filteredSites;
};

/**
 * Returns site role policy that applies to given employee type and role
 *
 * @param rolePolicy - Site role policy
 * @param type - Employee type
 * @param role - Employee role
 * @returns Appropriate role policy based on employee type and role.
 */
export const getRolePolicy = (rolePolicy: ISiteRolePolicy, type?: EmployeeType, role?: SiteRole) => {
  let policy;

  if (type === EmployeeType.ADMIN) {
    policy = rolePolicy.organizationAdmin;
  } else if (role === SiteRole.ADMIN) {
    policy = rolePolicy.siteAdmin;
  } else if (role === SiteRole.MANAGER) {
    policy = rolePolicy.siteManager;
  }
  return policy;
};

/**
 * Returns if given employee is payable based on current site role policy
 *
 * @param employee - Employee to be paid
 * @param rolePolicy - Site role policy
 * @param currentEmployee - Employee issuing the payment
 * @returns `true` if employee is payable, otherwise `false`.
 */
export const isEmployeePayable = (employee: IEmployee, rolePolicy: ISiteRolePolicy, currentEmployee?: ICurrentEmployee) => {
  const empSite = employee.sites[0];
  const policy = getRolePolicy(rolePolicy, employee.type, empSite?.role);

  if (policy) {
    if (policy.payable && currentEmployee && employee.id === currentEmployee.id) {
      return policy.paySelf;
    }
    return policy.payable;
  }
  return true;
};

/**
 * Checks if a given employee role is classified as a site role.
 *
 * This function takes an `employeeRole` of type `EmployeeRole` and determines
 * if it falls within the predefined set of roles considered as site roles:
 * `EmployeeRole.MANAGER`, `EmployeeRole.EMPLOYEE`, or `EmployeeRole.SITE_ADMIN`.
 *
 * @param {EmployeeRole} employeeRole - The role of the employee to check.
 * @returns {boolean} - Returns `true` if the role is either `MANAGER`, `EMPLOYEE`, or `SITE_ADMIN`; otherwise, returns `false`.
 */
export const isSiteRole = (employeeRole: EmployeeRole): boolean => [EmployeeRole.MANAGER, EmployeeRole.EMPLOYEE, EmployeeRole.SITE_ADMIN].includes(employeeRole);

/**
 * Retrieves the default disbursement method for a given site.
 *
 * @param {string} [siteId] - The ID of the site.
 * @param {ISite[]} [sites] - An array of site objects.
 * @returns {object} The default disbursement method option from `DisbursementOptions`, or `instant` if not found.
 */
export const getDefaultDisbursementMethod = (siteId?: string, sites?: Array<ISite | IEmployerSite | IEmployeeSite>) => {
  const defaultMethod = sites?.find((s) => s.id === siteId)?.settings.disbursementMethod || DisbursementMethod.INSTANT;
  return DisbursementOptions.find((d) => d.value === defaultMethod);
};

/**
 * Retrieves the requireEmployeeId setting for a given site.
 *
 * @param {string} [siteId] - The ID of the site.
 * @param {ISite[]} [sites] - An array of site objects.
 * @returns {boolean} The value of `requireEmployeeId` for the site, or `false` if not found.
 */
export const isEmployeeIdRequired = (siteId?: string, sites?: Array<ISite | IEmployerSite | IEmployeeSite>): boolean => sites?.find((s) => s.id === siteId)?.settings.requireEmployeeId || false;
